import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { useMerchantRoutes } from "@redotech/redo-merchant-app-common/merchant-routes";
import { createContext, memo, ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanMode } from "../../scan/common";
import { MissingItemsModal } from "../../scan/modals/missing-items-modal";
import { useScanMode } from "../../scan/scanning-context/use-scan-mode";

export interface MissingItemsModalContextValue {
  openModal: (
    group: FulfillmentGroupData,
    options?: {
      onConfirm?: () => void;
      onFulfillmentGroupUpdated?: (group: FulfillmentGroupData) => void;
    },
  ) => void;
}

export const MissingItemsModalContext =
  createContext<MissingItemsModalContextValue>({ openModal: () => {} });

export const MissingItemsModalProvider = memo(
  function MissingItemsModalProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const routes = useMerchantRoutes();
    const { setScanMode } = useScanMode();
    const canScanAndVerify = useHasPermission(
      OrderManagementPermissions.SCAN_AND_VERIFY,
    );
    const [fulfillmentGroup, setFulfillmentGroup] =
      useState<FulfillmentGroupData | null>(null);
    const [open, setOpen] = useState(false);
    const [onConfirmCallback, setOnConfirmCallback] = useState<
      (() => void) | undefined
    >();
    const [
      onFulfillmentGroupUpdatedCallback,
      setOnFulfillmentGroupUpdatedCallback,
    ] = useState<((group: FulfillmentGroupData) => void) | undefined>();

    const openModal = useHandler(
      (
        group: FulfillmentGroupData,
        options?: {
          onConfirm?: () => void;
          onFulfillmentGroupUpdated?: (group: FulfillmentGroupData) => void;
        },
      ) => {
        setFulfillmentGroup(group);
        setOnConfirmCallback(() => options?.onConfirm);
        setOnFulfillmentGroupUpdatedCallback(
          () => options?.onFulfillmentGroupUpdated,
        );
        setOpen(true);
      },
    );

    const onClose = useHandler(() => {
      setOpen(false);
      setFulfillmentGroup(null);
      setOnConfirmCallback(undefined);
      setOnFulfillmentGroupUpdatedCallback(undefined);
    });

    const onConfirm = useHandler((andFulfill: boolean) => {
      onConfirmCallback?.();
      onClose();
      if (andFulfill && canScanAndVerify && fulfillmentGroup) {
        setScanMode(ScanMode.VERIFY_PRINT);
        navigate(
          routes.getOrdersOutboundLabelsSubRoute(
            `scan/${fulfillmentGroup._id}`,
          ),
        );
      }
    });

    const onViewFullDetails = useHandler(() => {
      if (fulfillmentGroup) {
        onClose();
        navigate(`../pick-sessions/problem-order/${fulfillmentGroup._id}`);
      }
    });

    const handleFulfillmentGroupUpdated = useHandler(
      (updatedGroup: FulfillmentGroupData) => {
        setFulfillmentGroup(updatedGroup);
        onFulfillmentGroupUpdatedCallback?.(updatedGroup);
      },
    );

    return (
      <MissingItemsModalContext.Provider value={{ openModal }}>
        {children}
        {fulfillmentGroup && (
          <MissingItemsModal
            canScanAndVerify={canScanAndVerify}
            fulfillmentGroup={fulfillmentGroup}
            isOpen={open}
            onClose={onClose}
            onConfirm={onConfirm}
            onFulfillmentGroupUpdated={handleFulfillmentGroupUpdated}
            onViewFullDetails={onViewFullDetails}
          />
        )}
      </MissingItemsModalContext.Provider>
    );
  },
);
